/**
 * TelemetryRow — a single metric's row inside the synchronized chart
 * group. Emits THREE sibling grid cells as a fragment:
 *
 *   [ label cell ] [ y-tick cell ] [ chart cell ]
 *
 * so the parent's grid can lay every row out in a single shared column
 * structure. That lets the chart group's overlay (crosshair + brush
 * + tooltip) span every chart cell with a single absolutely-positioned
 * element — no per-row pointer plumbing.
 *
 * Visual anatomy per chart cell:
 *
 *   y-tick col │  chart cell
 *      max  ── │ ──────────────────────────────────
 *               │
 *      mid  ── │     ╱╲╱╲╱╲                     ● now
 *               │
 *      min  ── │ ──────────────────────────────────
 *
 * The Y-tick labels live in their own grid column (32 px) so they
 * never overlap the polyline. The horizontal gridlines at min / mid /
 * max are visible (not the previous near-invisible whisper). Vertical
 * gridlines align with the chart group's X-axis ticks (0/25/50/75/100 %).
 *
 * Y-scale resolution:
 *   - Series with fixed [yMin, yMax] (percentages, signal scores) use
 *     those bounds verbatim.
 *   - Auto-scaled series use a ROBUST scale: 5th–95th percentile of the
 *     non-null data, extended to include the latest reading so the
 *     most recent value is always on-chart. Points outside the range
 *     still render (the polyline clips at the SVG edge) and a small
 *     triangle marker at the chart edge signals "off-scale value
 *     here", so a single corrupt 2420 h reading no longer flattens
 *     Lamp Hours into invisibility.
 */
import { useMemo } from 'react';
import type { TelemetryPoint } from '../../api/hooks';
import type { TelemetrySeries } from './series';

interface TelemetryRowProps {
  series: TelemetrySeries;
  points: TelemetryPoint[];
  sinceMs: number;
  spanMs: number;
  /** Render height of the chart cell in pixels. */
  height?: number;
}

const VIEWBOX_W = 100;
const VIEWBOX_H = 100;
const GRID_X_TICKS = [0, 25, 50, 75, 100] as const;
const GRID_Y_TICKS = [0, 50, 100] as const; // top, mid, bottom of chart

function buildPathSegments(
  pts: Array<{ ts: number; v: number | null }>,
  sinceMs: number,
  spanMs: number,
  yMin: number,
  yMax: number,
): string[] {
  const yRange = yMax - yMin || 1;
  const segs: string[] = [];
  let current: string[] = [];
  for (const p of pts) {
    if (p.v === null || !Number.isFinite(p.v)) {
      if (current.length >= 2) segs.push(current.join(' '));
      current = [];
      continue;
    }
    const x = ((p.ts - sinceMs) / spanMs) * VIEWBOX_W;
    const yNorm = (p.v - yMin) / yRange;
    const y = (1 - yNorm) * VIEWBOX_H;
    current.push(`${current.length === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`);
  }
  if (current.length >= 2) segs.push(current.join(' '));
  return segs;
}

/**
 * Linear interpolation between sorted array indices for percentile
 * estimation. Returns 0 for an empty input — caller is responsible for
 * the "no data" path.
 */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const frac = idx - lo;
  return sorted[lo]! * (1 - frac) + sorted[hi]! * frac;
}

export function TelemetryRow({
  series,
  points,
  sinceMs,
  spanMs,
  height = 72,
}: TelemetryRowProps) {
  const rowPoints = useMemo(
    () =>
      points.map((p) => ({
        ts: Date.parse(p.bucket),
        v: series.accessor(p),
      })),
    [points, series],
  );

  // Compute robust Y-axis bounds.
  const scale = useMemo(() => {
    const values: number[] = [];
    let latest: number | null = null;
    for (const p of rowPoints) {
      if (p.v === null || !Number.isFinite(p.v)) continue;
      values.push(p.v);
      latest = p.v; // points are time-ordered
    }
    if (values.length === 0) {
      return { yMin: 0, yMax: 1, mid: 0.5, latest: null, hasData: false, outliers: [] as Array<{ x: number; over: boolean }> };
    }

    let yMin: number;
    let yMax: number;
    if (series.yMin !== undefined && series.yMax !== undefined) {
      // Series declares a fixed range — respect it verbatim.
      yMin = series.yMin;
      yMax = series.yMax;
    } else if (series.yMin !== undefined || series.yMax !== undefined) {
      // Half-fixed (rare). Fill the missing edge from the data so the
      // y-scale is still bounded sanely.
      const sorted = [...values].sort((a, b) => a - b);
      yMin = series.yMin ?? sorted[0]!;
      yMax = series.yMax ?? sorted[sorted.length - 1]!;
    } else {
      // ROBUST auto-scale: 5th–95th percentile, extended to include
      // the latest value (which is the operator's primary anchor).
      const sorted = [...values].sort((a, b) => a - b);
      const lo = percentile(sorted, 0.05);
      const hi = percentile(sorted, 0.95);
      yMin = Math.min(lo, latest!);
      yMax = Math.max(hi, latest!);
      // Flat-line case (all values identical, or window of size 1).
      if (yMin === yMax) {
        yMin -= 1;
        yMax += 1;
      } else {
        // Small headroom so the line doesn't kiss the gridlines.
        const pad = (yMax - yMin) * 0.08;
        yMin -= pad;
        yMax += pad;
      }
    }

    // Collect out-of-range points so we can mark them at the chart edge.
    const outliers: Array<{ x: number; over: boolean }> = [];
    for (const p of rowPoints) {
      if (p.v === null || !Number.isFinite(p.v)) continue;
      if (p.v > yMax || p.v < yMin) {
        const x = ((p.ts - sinceMs) / spanMs) * VIEWBOX_W;
        if (x < 0 || x > VIEWBOX_W) continue; // outside the time window
        outliers.push({ x, over: p.v > yMax });
      }
    }

    return {
      yMin,
      yMax,
      mid: (yMin + yMax) / 2,
      latest,
      hasData: true,
      outliers,
    };
  }, [rowPoints, series, sinceMs, spanMs]);

  const segments = useMemo(
    () =>
      scale.hasData
        ? buildPathSegments(rowPoints, sinceMs, spanMs, scale.yMin, scale.yMax)
        : [],
    [rowPoints, sinceMs, spanMs, scale],
  );

  // Latest-value marker — small filled dot at the rightmost non-null
  // point. Skipped when there's no data or when the latest is outside
  // the y-range (an outlier triangle is more honest than a clipped dot).
  const latestPoint = useMemo(() => {
    if (!scale.hasData || scale.latest === null) return null;
    for (let i = rowPoints.length - 1; i >= 0; i--) {
      const p = rowPoints[i]!;
      if (p.v === null || !Number.isFinite(p.v)) continue;
      if (p.v > scale.yMax || p.v < scale.yMin) return null; // outlier — handled by triangle
      const x = ((p.ts - sinceMs) / spanMs) * VIEWBOX_W;
      if (x < 0 || x > VIEWBOX_W) return null;
      const yNorm = (p.v - scale.yMin) / (scale.yMax - scale.yMin || 1);
      const y = (1 - yNorm) * VIEWBOX_H;
      return { x, y };
    }
    return null;
  }, [rowPoints, sinceMs, spanMs, scale]);

  return (
    <>
      {/* Label cell. */}
      <div
        className="flex items-center gap-1.5 text-[11px] text-fg"
        style={{ height }}
      >
        <span
          className="inline-block w-2 h-2 rounded-sm shrink-0"
          style={{ backgroundColor: series.color }}
          aria-hidden="true"
        />
        <span className="truncate">{series.label}</span>
      </div>
      {/* Y-tick label cell. Three labels stacked at top / middle /
          bottom; flex-justify-between gives them exact alignment with
          the chart's gridlines at y=0 / y=50 / y=100. */}
      <div
        className="flex flex-col justify-between items-end text-[10px] text-muted tabular-nums leading-none py-[2px]"
        style={{ height }}
      >
        {scale.hasData ? (
          <>
            <span>{series.format(scale.yMax)}</span>
            <span className="opacity-70">{series.format(scale.mid)}</span>
            <span>{series.format(scale.yMin)}</span>
          </>
        ) : (
          <span className="self-center opacity-60">—</span>
        )}
      </div>
      {/* Chart cell. */}
      <div className="relative" style={{ height }}>
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${series.label} over the selected window`}
          style={{ overflow: 'hidden', display: 'block' }}
        >
          {/* Horizontal gridlines at y=0/50/100 % (the same positions
              the Y-tick labels sit at). */}
          {GRID_Y_TICKS.map((y) => (
            <line
              key={`h${y}`}
              x1="0"
              x2={VIEWBOX_W}
              y1={y}
              y2={y}
              stroke="#E4E4E4"
              strokeWidth="0.4"
              strokeDasharray={y === 0 || y === 100 ? '0' : '0.8 1.2'}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Vertical gridlines at the X-axis tick positions. */}
          {GRID_X_TICKS.map((x) => (
            <line
              key={`v${x}`}
              x1={x}
              x2={x}
              y1="0"
              y2={VIEWBOX_H}
              stroke="#E4E4E4"
              strokeWidth="0.3"
              strokeDasharray="0.8 1.2"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {segments.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={series.color}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Latest-value dot — small filled circle at the right end
              of the polyline so the eye finds "where is the line now". */}
          {latestPoint && (
            <circle
              cx={latestPoint.x}
              cy={latestPoint.y}
              r="1.4"
              fill={series.color}
              stroke="#ffffff"
              strokeWidth="0.6"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {/* Off-scale outlier markers. A small triangle sits at the
              chart edge wherever a value clipped out of [yMin, yMax].
              Points up (▴) for "above yMax", down (▾) for "below yMin". */}
          {scale.outliers.map((o, i) => (
            <polygon
              key={`out${i}`}
              points={
                o.over
                  ? `${o.x - 1.2},4 ${o.x + 1.2},4 ${o.x},0.5`
                  : `${o.x - 1.2},${VIEWBOX_H - 4} ${o.x + 1.2},${VIEWBOX_H - 4} ${o.x},${VIEWBOX_H - 0.5}`
              }
              fill={series.color}
              fillOpacity="0.85"
              stroke="#ffffff"
              strokeWidth="0.4"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        {/* Latest-value badge floats top-right; redundant with the
            on-line dot but useful when the dot is at the right edge
            and might be cropped or hard to read. */}
        {scale.hasData && scale.latest !== null && (
          <div className="absolute top-0 right-0 text-[10px] text-fg font-medium tabular-nums pointer-events-none bg-white/90 px-1 rounded-sm">
            {series.format(scale.latest)}
          </div>
        )}
        {!scale.hasData && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted pointer-events-none">
            no data
          </div>
        )}
      </div>
    </>
  );
}
